import { FC, KeyboardEvent, useRef } from "react";

import { theme } from "@ovou-inc/ovou-ui";

import { Flex } from "../Container";
import { Text } from "../Typography";

export interface ISegment {
  label: string;
  value: string;
  /**
   * Text color when this segment is selected (Figma: green for the permissive
   * option, orange for the restrictive one). Injected so the control stays
   * presentation-agnostic.
   */
  activeColor: string;
}

export interface ISegmentedControlProps {
  segments: [ISegment, ISegment];
  value: string;
  onChange: (value: string) => void;
  ariaLabel?: string;
}

/**
 * Two-segment pill control (Figma 15355:7091): a grey track holding two rounded
 * segments. The selected segment is a white pill with its own active text color;
 * the other shows grey text on the track. Controlled via value/onChange.
 *
 * Follows the WAI-ARIA radio-group pattern: roving tabindex (only the selected
 * segment is in the tab order) with Arrow keys moving selection between
 * segments, plus Enter/Space to (re)select the focused one.
 */
const SegmentedControl: FC<ISegmentedControlProps> = ({
  segments,
  value,
  onChange,
  ariaLabel,
}) => {
  const segmentRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Move selection (and focus) to a sibling segment — used by the Arrow keys.
  const selectAt = (index: number) => {
    const target = segments[index];
    if (!target) {
      return;
    }
    onChange(target.value);
    segmentRefs.current[index]?.focus();
  };

  return (
    <Flex
      role="radiogroup"
      aria-label={ariaLabel}
      alignItems="center"
      height="32px"
      padding="2px"
      borderRadius="60px"
      bg={theme.palette.ui.greys.grey4}
      flexShrink={0}
    >
      {segments.map((segment, index) => {
        const selected = segment.value === value;
        return (
          <Flex
            key={segment.value}
            ref={(el: HTMLDivElement | null) => {
              segmentRefs.current[index] = el;
            }}
            role="radio"
            aria-checked={selected}
            aria-label={segment.label}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(segment.value)}
            onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
              if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                onChange(segment.value);
              } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                e.preventDefault();
                selectAt((index + 1) % segments.length);
              } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                e.preventDefault();
                selectAt((index - 1 + segments.length) % segments.length);
              }
            }}
            alignItems="center"
            justifyContent="center"
            height="28px"
            paddingX="12px"
            paddingY="4px"
            borderRadius="60px"
            cursor="pointer"
            bg={selected ? theme.palette.brand.primary.white : "transparent"}
            _focusVisible={{ outline: "none", boxShadow: "none" }}
          >
            <Text
              fontSize="12px"
              fontWeight={600}
              whiteSpace="nowrap"
              color={
                selected ? segment.activeColor : theme.palette.ui.greys.grey1
              }
            >
              {segment.label}
            </Text>
          </Flex>
        );
      })}
    </Flex>
  );
};

export default SegmentedControl;
